<template>
  <div class="pageRoot linkListPage collectionModule">
    <div ref="pageCard" class="pageShell">
      <div ref="pageCardMain" class="pageShell__main">
        <div
          ref="scrollArea"
          class="pageShell__scroll"
          :class="{ 'pageShell__scroll--drawerOpen': filterDrawerOpen }"
        >
          <div
            class="listWorkspace"
            :class="{ 'listWorkspace--scrolled': controlBarScrolled }"
          >
            <section
              ref="filterAnchor"
              class="filterCard filterAnchor"
              :class="{ 'filterAnchor--drawerOpen': filterDrawerOpen }"
            >
              <page-list-filter-panel
                v-bind="filterPanelProps"
                v-on="filterPanelListeners"
              />
            </section>

            <section
              ref="tableZone"
              class="tableCard tableZone"
              :class="{ 'tableCard--scrolled': controlBarScrolled }"
            >
              <div
                class="scrollControlBar"
                :class="{ 'scrollControlBar--scrolled': controlBarScrolled }"
              >
                <div class="scrollControlBar__lead">
                  <span class="scrollControlBar__label">共</span>
                  <strong class="scrollControlBar__count">{{ listTotalCount }}</strong>
                  <span class="scrollControlBar__label">条</span>
                </div>

                <div
                  v-if="!controlBarScrolled"
                  class="scrollControlBar__stage scrollControlBar__stage--full"
                >
                  <control-bar-display
                    layout="info"
                    :scope-filters="activeScopeFilters"
                    :has-active-scope-filters="hasActiveScopeFilters"
                    :scope-filter-tooltip="scopeFilterTooltip"
                    :selection-count="0"
                    @open-filter="openFilterDrawer"
                  />
                </div>

                <template v-else>
                  <div class="scrollControlBar__stage scrollControlBar__stage--left">
                    <control-bar-display
                      part="info"
                      layout="info"
                      :scope-filters="activeScopeFilters"
                      :has-active-scope-filters="hasActiveScopeFilters"
                      :scope-filter-tooltip="scopeFilterTooltip"
                      :selection-count="0"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__navSlot">
                    <control-bar-nav
                      @scroll-top="scrollToTableTop"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__stage scrollControlBar__stage--right" />
                </template>

                <div class="scrollControlBar__page">
                  <template v-if="listTotalCount > 0">
                    <span>第 {{ listRangeStart }}–{{ listRangeEnd }} 条</span>
                    <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                    <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                  </template>
                  <template v-else>
                    <span class="scrollControlBar__emptyHint">暂无数据</span>
                    <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                    <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                  </template>
                </div>
              </div>

              <div v-if="!loading && tableData.length" class="tableSection">
                <el-table
                  ref="multipleTable"
                  class="linkTable linkTable--hybrid"
                  :data="tableData"
                  tooltip-effect="dark"
                  style="width: 100%"
                  row-key="id"
                >
                  <el-table-column
                    label="页面名称"
                    prop="name"
                    align="center"
                    header-align="center"
                    min-width="180"
                    show-overflow-tooltip
                  >
                    <template slot-scope="scope">
                      <span class="nameCell">{{ scope.row.name || '—' }}</span>
                    </template>
                  </el-table-column>

                  <el-table-column
                    label="页面类型"
                    prop="type"
                    align="center"
                    header-align="center"
                    min-width="140"
                    show-overflow-tooltip
                  >
                    <template slot-scope="scope">
                      <span
                        class="typeTag"
                        :class="'typeTag--' + getPageTypeVariant(scope.row.type)"
                      >
                        {{ scope.row.type || '—' }}
                      </span>
                    </template>
                  </el-table-column>

                  <el-table-column
                    label="已关联页面组"
                    align="center"
                    header-align="center"
                    min-width="200"
                    class-name="categoryColumn"
                  >
                    <template slot-scope="scope">
                      <div
                        v-if="
                          scope.row.pageSimpleVOSet &&
                          scope.row.pageSimpleVOSet.length
                        "
                        class="relationCell"
                      >
                        <router-link
                          v-for="item in getVisiblePages(scope.row.pageSimpleVOSet)"
                          :key="'page-' + item.id"
                          :to="{ path: '/collection/page/info/' + item.id }"
                          class="relationCell__link"
                          @click.native.stop
                        >
                          <el-tag
                            size="mini"
                            effect="plain"
                            class="categoryTag relationTag"
                            :class="'relationTag--tone-' + getPageTagTone(item.id)"
                          >
                            {{ item.name }}
                          </el-tag>
                        </router-link>
                        <el-tag
                          v-if="getHiddenPageCount(scope.row.pageSimpleVOSet) > 0"
                          size="mini"
                          effect="plain"
                          class="categoryTag"
                        >
                          +{{ getHiddenPageCount(scope.row.pageSimpleVOSet) }}
                        </el-tag>
                      </div>
                      <span v-else class="cellEmptyPill">未关联</span>
                    </template>
                  </el-table-column>

                  <el-table-column
                    label="已关联数据集"
                    align="center"
                    header-align="center"
                    min-width="200"
                    class-name="categoryColumn"
                  >
                    <template slot-scope="scope">
                      <div
                        v-if="
                          scope.row.dataSetSimpleVOSet &&
                          scope.row.dataSetSimpleVOSet.length
                        "
                        class="relationCell"
                      >
                        <router-link
                          v-for="item in getVisiblePages(scope.row.dataSetSimpleVOSet)"
                          :key="'dataset-' + item.id"
                          :to="{ path: '/channel/data/info/' + item.id }"
                          class="relationCell__link"
                          @click.native.stop
                        >
                          <el-tag
                            size="mini"
                            effect="plain"
                            class="categoryTag relationTag"
                            :class="'relationTag--tone-' + getPageTagTone(item.id)"
                          >
                            {{ item.name }}
                          </el-tag>
                        </router-link>
                        <el-tag
                          v-if="getHiddenPageCount(scope.row.dataSetSimpleVOSet) > 0"
                          size="mini"
                          effect="plain"
                          class="categoryTag"
                        >
                          +{{ getHiddenPageCount(scope.row.dataSetSimpleVOSet) }}
                        </el-tag>
                      </div>
                      <span v-else class="cellEmptyPill">未关联</span>
                    </template>
                  </el-table-column>

                  <el-table-column
                    label="更新时间"
                    prop="updateTime"
                    align="center"
                    header-align="center"
                    min-width="160"
                    show-overflow-tooltip
                    class-name="timeColumn"
                  />

                  <el-table-column
                    label="操作"
                    label-class-name="linkListActionColumnHead"
                    align="center"
                    header-align="center"
                    width="340"
                    class-name="actionColumn"
                  >
                    <template slot="header">
                      <span class="linkListActionColumnHead__label">操作</span>
                    </template>
                    <template slot-scope="scope">
                      <div class="linkTableActionCell">
                        <el-button
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost"
                          icon="el-icon-document-copy"
                          @click.stop="copyItem(scope.row.id)"
                        >
                          复制
                        </el-button>
                        <el-button
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost"
                          icon="el-icon-view"
                          @click.stop="goInfo(scope.row.id)"
                        >
                          查看
                        </el-button>
                        <el-button
                          v-if="showActions"
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost"
                          icon="el-icon-edit"
                          v-check-role
                          @click.stop="goEdit(scope.row.id)"
                        >
                          编辑
                        </el-button>
                        <el-button
                          v-if="showActions"
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost rowActionBtn--danger"
                          icon="el-icon-delete"
                          v-check-role
                          @click.stop="deletePageRow(scope.row)"
                        >
                          删除
                        </el-button>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
              </div>

              <div v-else-if="!loading && !tableData.length" class="tableEmpty">
                <div class="tableEmpty__icon"><i class="el-icon-document" /></div>
                <p class="tableEmpty__title">暂无页面</p>
                <p class="tableEmpty__desc">调整筛选条件或新增页面。</p>
                <el-button
                  type="primary"
                  size="small"
                  v-check-role
                  @click="$router.push('/collection/page/create')"
                >
                  新增页面
                </el-button>
              </div>

              <div v-else class="tableSkeletonWrap">
                <el-skeleton :rows="8" animated />
              </div>
            </section>
          </div>
        </div>

        <div
          v-if="filterDrawerOpen"
          class="filterDrawerLayer"
          role="dialog"
          aria-modal="true"
          aria-label="筛选与操作"
        >
          <div
            class="filterDrawerPanel"
            :class="{ 'filterDrawerPanel--scrollable': drawerPanelScrollable }"
            :style="drawerPanelStyle"
          >
            <div class="filterDrawerPanel__header">
              <span class="filterDrawerPanel__title">筛选与操作</span>
              <div class="filterDrawerPanel__headerActions">
                <span v-if="activeFilterCount" class="filterDrawerPanel__chip">
                  已筛选 {{ activeFilterCount }} 项
                </span>
                <button
                  type="button"
                  class="filterDrawerPanel__closeBtn"
                  @click="closeFilterDrawer"
                >
                  收起
                </button>
              </div>
            </div>
            <div ref="drawerScroll" class="filterDrawerPanel__scroll">
              <page-list-filter-panel
                v-bind="{ ...filterPanelProps, showPanelSummary: true }"
                v-on="filterPanelListeners"
              />
            </div>
          </div>
          <div
            class="filterDrawerBackdrop"
            aria-hidden="true"
            @click="closeFilterDrawer"
            @wheel.prevent
          />
        </div>
      </div>

      <div class="pageShell__footer tableCard__footer footerBar">
        <div class="footerSummary">
          共 <strong class="footerSummary__count">{{ listTotalCount }}</strong> 条 · 第
          {{ currentPage }} / {{ totalPages }} 页
        </div>
        <el-pagination
          class="linkPagination"
          layout="prev, pager, next, sizes, jumper"
          :total="listTotalCount"
          :page-sizes="[25, 50, 100]"
          :page-size="currentPageSize"
          :current-page.sync="currentPage"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { copyPage, deletepage, getPageList, queryType } from '../../../api/pageManage'
import PageListFilterPanel from './PageListFilterPanel.vue'
import ControlBarDisplay from '../../resourceManage/genericResourceManage/ControlBarDisplay.vue'
import ControlBarNav from '../../resourceManage/genericResourceManage/ControlBarNav.vue'
import linkListScrollControlMixin from '../../resourceManage/genericResourceManage/linkListScrollControlMixin'
import {
  getHiddenPageCount,
  getPageTagTone,
  getPageTypeVariant,
  getVisiblePages,
} from './pageListHelpers'

export default {
  name: 'PageManageList',
  components: {
    PageListFilterPanel,
    ControlBarDisplay,
    ControlBarNav,
  },
  mixins: [linkListScrollControlMixin],
  data() {
    return {
      pageName: '',
      pageType: '',
      tableData: [],
      currentPage: 1,
      currentPageSize: 25,
      total: 0,
      loading: false,
      pageTypeOptions: [],
    }
  },
  computed: {
    showActions() {
      return this.$route.meta.role == 2
    },
    listTotalCount() {
      const parsed = Number(this.total)
      return Number.isFinite(parsed) ? parsed : 0
    },
    listRangeStart() {
      if (this.listTotalCount === 0) return 0
      return (this.currentPage - 1) * this.currentPageSize + 1
    },
    listRangeEnd() {
      if (this.listTotalCount === 0) return 0
      return Math.min(this.currentPage * this.currentPageSize, this.listTotalCount)
    },
    totalPages() {
      if (this.listTotalCount === 0) return 1
      return Math.max(1, Math.ceil(this.listTotalCount / this.currentPageSize))
    },
    activeFilterCount() {
      return [this.pageName, this.pageType].filter(
        (value) => value !== '' && value !== null && value !== undefined
      ).length
    },
    activeScopeFilters() {
      const chips = []
      if (String(this.pageName || '').trim()) {
        chips.push({
          key: 'pageName',
          label: '页面名称',
          value: this.pageName.trim(),
        })
      }
      if (String(this.pageType || '').trim()) {
        chips.push({
          key: 'pageType',
          label: '页面类型',
          value: this.pageType.trim(),
        })
      }
      return chips
    },
    hasActiveScopeFilters() {
      return this.activeScopeFilters.length > 0
    },
    scopeFilterTooltip() {
      return this.activeScopeFilters
        .map((item) => `${item.label}: ${item.value}`)
        .join(' · ')
    },
    filterPanelProps() {
      return {
        pageName: this.pageName,
        pageType: this.pageType,
        activeFilterCount: this.activeFilterCount,
        pageTypeOptions: this.pageTypeOptions,
        panelTotalCount: this.listTotalCount,
      }
    },
    filterPanelListeners() {
      return {
        search: this.search,
        'go-create': () => this.$router.push('/collection/page/create'),
        'reset-filters': this.resetFilters,
        'reset-and-search': this.resetAndSearch,
        'update:pageName': (value) => {
          this.pageName = value
        },
        'update:pageType': (value) => {
          this.pageType = value
        },
      }
    },
  },
  watch: {
    loading(value) {
      if (!value) {
        this.onLinkListDataRendered()
      } else {
        this.$nextTick(() => {
          this.measureTableZoneWidth()
        })
      }
    },
    tableData() {
      this.$nextTick(() => {
        this.remeasureFilterBlockHeight()
        this.updateScrollControls()
        this.scheduleTableLayout()
      })
    },
    filterDrawerOpen(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.syncDrawerLayout()
        })
      }
    },
  },
  created() {
    this.bindLinkListScrollControl()
    this.loadPageTypes()
    this.getList()
  },
  methods: {
    getPageTypeVariant,
    getPageTagTone,
    getVisiblePages,
    getHiddenPageCount,
    loadPageTypes() {
      queryType().then((res) => {
        if (res.success) {
          this.pageTypeOptions = res.data || []
        }
      })
    },
    getList() {
      this.loading = true
      getPageList({
        currentPage: this.currentPage,
        currentPageSize: this.currentPageSize,
        pageName: this.pageName,
        pageType: this.pageType,
      })
        .then((res) => {
          if (res.success) {
            this.total = res.data.total
            this.tableData = res.data.pageVOList || []
          }
        })
        .finally(() => {
          this.loading = false
          this.$nextTick(() => {
            this.measureTableZoneWidth()
            this.scheduleTableLayout()
          })
        })
    },
    search() {
      this.currentPage = 1
      this.getList()
    },
    resetFilters() {
      this.pageName = ''
      this.pageType = ''
    },
    resetAndSearch() {
      this.resetFilters()
      this.search()
    },
    handleSizeChange(val) {
      this.currentPageSize = val
      this.$nextTick(() => {
        this.getList()
      })
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.getList()
    },
    goInfo(id) {
      this.$router.push(`/collection/page/info/${id}`)
    },
    goEdit(id) {
      this.$router.push(`/collection/page/edit/${id}`)
    },
    copyItem(id) {
      this.$confirm('此操作将复制该页面，是否继续？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => copyPage(id))
        .then((res) => {
          if (res && res.success) {
            this.$message.success('复制成功')
            this.getList()
          }
        })
        .catch((error) => {
          if (error !== 'cancel') return
          this.$message.info('已取消复制')
        })
    },
    deletePageRow(row) {
      const hasRelations =
        (row.pageSimpleVOSet && row.pageSimpleVOSet.length) ||
        (row.dataSetSimpleVOSet && row.dataSetSimpleVOSet.length)
      const hint = hasRelations
        ? '该页面仍有关联的页面组或数据集，删除后运营侧将不再展示，是否继续？'
        : '此操作将删除该页面，是否继续？'
      this.$confirm(hint, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => deletepage({ id: row.id }))
        .then((res) => {
          if (res && res.success) {
            this.$message.success('删除成功')
            this.getList()
          }
        })
        .catch(() => {})
    },
  },
}
</script>

<style scoped>
.relationCell {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.relationTag--tone-0 {
  --page-tag-bg: rgba(224, 242, 254, 0.85);
  --page-tag-ink: #0369a1;
}
.relationTag--tone-1 {
  --page-tag-bg: rgba(237, 233, 254, 0.9);
  --page-tag-ink: #6d28d9;
}
.relationTag--tone-2 {
  --page-tag-bg: rgba(254, 243, 199, 0.9);
  --page-tag-ink: #b45309;
}
.relationTag--tone-3 {
  --page-tag-bg: rgba(204, 251, 241, 0.85);
  --page-tag-ink: #0f766e;
}
.relationTag--tone-4 {
  --page-tag-bg: rgba(255, 228, 230, 0.9);
  --page-tag-ink: #be123c;
}
.relationTag--tone-5 {
  --page-tag-bg: rgba(243, 244, 246, 0.95);
  --page-tag-ink: #57534e;
}

.relationTag.categoryTag {
  background: var(--page-tag-bg) !important;
  border-color: transparent !important;
  color: var(--page-tag-ink) !important;
}

.relationCell__link {
  text-decoration: none;
}
</style>
